import React, { useEffect, useState } from "react";
import styles from "../../../CSS/sections/Verticaltabs.module.css";

const ICON_ANIMATION_DURATION = 600; // ms

const VerticalTabs = ({ content }) => {
  const { title, description, tabs = [] } = content || {};

  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState("next"); // "next" | "prev"

  useEffect(() => {
    if (!tabs || tabs.length <= 1) return;

    const interval = setInterval(() => {
      const currentIndex = activeIndex;
      const nextIndex = (currentIndex + 1) % tabs.length;

      setPrevIndex(currentIndex);
      setActiveIndex(nextIndex);
      setDirection("next");
      setIsAnimating(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [tabs, activeIndex]);

  useEffect(() => {
    if (!isAnimating) return;

    const timeout = setTimeout(() => {
      setIsAnimating(false);
      setPrevIndex(null);
    }, ICON_ANIMATION_DURATION);

    return () => clearTimeout(timeout);
  }, [isAnimating]);

  if (!tabs || tabs.length === 0) {
    return null;
  }

  const activeTab = tabs[activeIndex];
  const prevTab = prevIndex !== null ? tabs[prevIndex] : null;

  const handleTabClick = (index) => {
    if (index === activeIndex) return;

    const navDirection = index > activeIndex ? "next" : "prev";

    setPrevIndex(activeIndex);
    setActiveIndex(index);
    setDirection(navDirection);
    setIsAnimating(true);
  };

  return (
    <section className={`${styles.verticalTabsSection} padding-y`}>
      <div className="container">
        <div className="row">
          <div
            className="col-12 col-lg-8 offset-lg-2 text-center"
            style={{ marginBottom: "60px" }}
          >
            <h2 className="heading2 text-dark mb-4">{title}</h2>
            <p className={`${styles.verticalTabsDescription} text-dark`}>
              {description}
            </p>
          </div>
        </div>
        <div
          className={`${styles.rowCustom} row align-items-center d-none d-lg-flex`}
        >
          <div className="col-12 col-lg-4 mb-4 mb-lg-0">
            <ul className={styles.tabList}>
              {tabs.map((tab, index) => (
                <li
                  key={tab.tabTitle}
                  className={`${styles.tabItem} ${
                    index === activeIndex ? styles.activeTab : ""
                  }`}
                  onClick={() => handleTabClick(index)}
                >
                  {tab.tabTitle}
                </li>
              ))}
            </ul>
          </div>
          <div
            className={`col-12 col-lg-3 d-flex justify-content-center mb-4 mb-lg-0 ${styles.iconWrapperContainer}`}
          >
            <div className={styles.iconWrapper}>
              {isAnimating && prevTab && (
                <img
                  key={prevTab.tabTitle}
                  src={prevTab.icon}
                  alt={prevTab.tabTitle}
                  className={`${styles.iconImage} ${
                    direction === "prev"
                      ? styles.iconImageExitToBottom
                      : styles.iconImageExitToTop
                  }`}
                />
              )}
              <img
                key={activeTab.tabTitle}
                src={activeTab.icon}
                alt={activeTab.tabTitle}
                className={`${styles.iconImage} ${
                  isAnimating
                    ? direction === "prev"
                      ? styles.iconImageEnterFromTop
                      : styles.iconImageEnterFromBottom
                    : ""
                }`}
              />
            </div>
          </div>
          <div className="col-12 col-lg-5">
            <div className={styles.textPanel}>
              <h4 className={`${styles.heading}`}>{activeTab.title1}</h4>
              <p className={`${styles.description}`}>
                {activeTab.description1}
              </p>
              <div className={styles.separator} />
              <h4 className={`${styles.heading}`}>{activeTab.title2}</h4>
              <p className={`${styles.description} mb-0`}>
                {activeTab.description2}
              </p>
            </div>
          </div>
        </div>

        <div className="row d-lg-none text-center">
          <div className="col-12">
            {tabs.map((tab) => (
              <div
                key={tab.tabTitle}
                className={`${styles.tabCard} text-center`}
              >
                <div className={`${styles.iconWrapper} mx-auto`}>
                  <img src={tab.icon} alt={tab.tabTitle} />
                </div>
                <h3 className={`${styles.tabTitle} heading3`}>{tab.tabTitle}</h3>
                <h4 className={`${styles.heading}`}>{tab.title1}</h4>
                <p className={`${styles.description}`}>{tab.description1}</p>
                <div className={styles.separator} />
                <h4 className={`${styles.heading}`}>{tab.title2}</h4>
                <p className={`${styles.description} mb-0`}>
                  {tab.description2}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VerticalTabs;